import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoBack } from '../../hooks/useReturnNavigation';
import { useAuth } from '../../context/AuthContext';
import { userService } from '../../services';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { useDepartments } from '../../hooks';
import type { UserRole } from '../../types/api';

interface InviteFormData {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  department: string;
  position: string;
}

export const InviteUser: React.FC = () => {
  const navigate = useNavigate();
  // Cancel and Send return to the list that opened this page, filters intact.
  const goBack = useGoBack('/users');
  const { user: currentUser } = useAuth();
  const { data: departments = [] } = useDepartments();

  const [formData, setFormData] = useState<InviteFormData>({
    email: '',
    firstName: '',
    lastName: '',
    role: 'employee',
    department: '',
    position: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  if (!currentUser || (currentUser.role !== 'account_admin' && currentUser.role !== 'manager')) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Card className="p-8 max-w-md">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Access Denied</h2>
            <p className="text-stone-600 mb-6">
              You don't have permission to invite users.
            </p>
            <Button variant="primary" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const roleOptions = [
    { value: 'account_admin', label: 'Account Admin', disabled: currentUser.role !== 'account_admin' },
    { value: 'manager', label: 'Manager' },
    { value: 'employee', label: 'Employee' },
    { value: 'viewer', label: 'Viewer' }
  ];

  const departmentOptions = [
    { value: '', label: 'Select Department', disabled: false },
    ...departments.map(dept => ({ value: dept.name, label: dept.name, disabled: false }))
  ];

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    if (!formData.department?.trim()) {
      newErrors.department = 'Department is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setErrorMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      await userService.inviteUser(formData);
      setSuccessMessage(
        `Invitation sent successfully to ${formData.email}! They will receive an email with instructions to activate their account.`
      );

      // Reset form
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        role: 'employee',
        department: '',
        position: ''
      });

      // Redirect to user management after 3 seconds
      setTimeout(() => {
        goBack();
      }, 3000);
    } catch (error) {
      console.error('Invite user error:', error);
      const err = error as { response?: { data?: { message?: string } } };
      setErrorMessage(
        err.response?.data?.message ||
        'Failed to send invitation. The email may already be registered.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={goBack}
              className="mr-4"
            >
              <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Invite User</h1>
              <p className="mt-1 text-sm text-stone-500">
                Send an invitation to a new team member
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {successMessage && (
          <div className="mb-6 bg-success-50 border border-success-200 text-success-700 px-4 py-3 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              {successMessage}
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="mb-6 bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg">
            {errorMessage}
          </div>
        )}

        <Card className="p-6">
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  error={errors.firstName}
                  placeholder="John"
                  required
                />

                <Input
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={errors.lastName}
                  placeholder="Doe"
                  required
                />
              </div>

              <Input
                label="Email Address"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                placeholder="john.doe@example.com"
                helperText="An invitation email will be sent to this address"
                required
              />

              <Select
                label="Role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                error={errors.role}
                options={roleOptions}
                helperText={
                  currentUser.role !== 'account_admin'
                    ? 'Only Account Admins can invite other Account Admins'
                    : undefined
                }
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Select
                  label="Department"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  error={errors.department}
                  options={departmentOptions}
                  required
                  helperText="Required for all users. Account Admins will be auto-assigned to Admin department."
                />

                <Input
                  label="Position"
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  placeholder="e.g., Senior Developer"
                />
              </div>

              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-primary-900 mb-2">What happens next?</h3>
                <ul className="text-sm text-primary-800 space-y-1 list-disc list-inside">
                  <li>An invitation email will be sent to the user</li>
                  <li>They'll receive a link to activate their account</li>
                  <li>They'll set their password and can start using OKR Hub</li>
                  <li>You can track invitation status in the user management page</li>
                </ul>
              </div>
            </div>

            <div className="mt-8 flex justify-end space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={goBack}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <LoadingSpinner size="sm" className="mr-2" />
                    Sending Invitation...
                  </span>
                ) : (
                  'Send Invitation'
                )}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};
